import React, { useState, useEffect } from 'react';
import Accordion from '../layout/accordion/Accordion';
import AccordionItem from '../layout/accordion/AccordionItem';
import StakeholderList from './StakeholderList';
import StakeholderSearch from './StakeholderSearch';
import StakeholderCreationButton from './StakeholderCreationButton';
import { fetchUserStakeholders, fetchStakeholders } from '../../utils/stakeholders';
import { fetchUserCompanies } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './Canvasses.module.css';

const StakeholderSelector = ({ onSelect, companyStakeholders }) => {
  const [publicStakeholders, setPublicStakeholders] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [selectedMyStakeholderId, setSelectedMyStakeholderId] = useState(null);
  const [selectedPublicStakeholderId, setSelectedPublicStakeholderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    loadData();
    loadPublicStakeholders();
  }, []);

  
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [companiesResult, stakeholdersResult] = await Promise.all([
        fetchUserCompanies({ pageSize: 50 }),
        fetchUserStakeholders({ pageSize: 100 })
      ]);
      
      setUserCompanies(companiesResult.companies || []);
      // Note: user stakeholders will be handled by companyStakeholders prop
      
      // Auto-select first company if available
      if (companiesResult.companies && companiesResult.companies.length > 0) {
        setSelectedCompanyId(companiesResult.companies[0].id);
      }
    } catch (err) {
      logger.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPublicStakeholders = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await fetchStakeholders({ isPublic: true, pageSize: 50 });
      setPublicStakeholders(result.stakeholders || []);
    } catch (err) {
      logger.error('Error loading public stakeholders:', err);
      setError('Failed to load public stakeholders');
    } finally {
      setLoading(false);
    }
  };

  
  const handleMyStakeholderSelect = (stakeholder) => {
    setSelectedMyStakeholderId(stakeholder.id);
    setSelectedPublicStakeholderId(null); // Clear public selection
    onSelect(stakeholder);
  };

  const handlePublicStakeholderSelect = (stakeholder) => {
    setSelectedPublicStakeholderId(stakeholder.id);
    setSelectedMyStakeholderId(null); // Clear my stakeholders selection
    onSelect(stakeholder);
  };

  const handleStakeholderSelect = (stakeholder) => {
    // This is used for components that don't specify which section
    // Check if stakeholder is in companyStakeholders to determine the section
    const isMyStakeholder = companyStakeholders.some(s => s.id === stakeholder.id);
    if (isMyStakeholder) {
      handleMyStakeholderSelect(stakeholder);
    } else {
      handlePublicStakeholderSelect(stakeholder);
    }
  };

  if (loading && companyStakeholders.length === 0) {
    return <div>Loading stakeholders...</div>;
  }

  return (
    <div className={styles.stakeholderSelector}>
      <h4>Select Stakeholder:</h4>
      
      <Accordion>
        <AccordionItem title="My Stakeholders">
          <StakeholderList 
            stakeholders={companyStakeholders}
            onSelect={handleMyStakeholderSelect}
            selectedStakeholderId={selectedMyStakeholderId}
            userCompanies={userCompanies}
          />
        </AccordionItem>
        
        <AccordionItem title="Public Stakeholders">
          <StakeholderSearch 
            stakeholders={publicStakeholders}
            onSelect={handlePublicStakeholderSelect}
            loading={loading}
            error={error}
            selectedStakeholderId={selectedPublicStakeholderId}
            userCompanies={userCompanies}
          />
        </AccordionItem>
        
        <AccordionItem title="Create New Stakeholder">
          <StakeholderCreationButton onSuccess={handleStakeholderSelect} parentCompanyId={selectedCompanyId} selectedStakeholderId={selectedMyStakeholderId} />
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default StakeholderSelector;
